import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static values = { crawlId: Number }
  static targets = [
    "status",
    "pagesCrawled",
    "totalPages",
    "progress",
    "progressBar",
    "duration",
    "successfulPages",
    "errorPages",
    "redirectPages"
  ]

  // Check if we're in development mode
  isDevelopment() {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1' ||
           window.location.hostname.includes('localhost')
  }

  connect() {
    if (this.isDevelopment()) {
      console.log("CrawlProgressController connected for crawl", this.crawlIdValue)
    }
    
    // Check if crawl is already running/paused and initialize duration timer
    const statusElement = this.hasStatusTarget ? this.statusTarget : null
    if (statusElement) {
      const status = statusElement.textContent.toLowerCase().trim()
      if ((status === "running" || status === "paused") && this.hasDurationTarget) {
        // Get started_at from the controller element's data attribute
        const startedAt = this.element.getAttribute('data-crawl-started-at')
        if (startedAt) {
          this.lastStartedAt = startedAt
          this.startDurationTimer()
        }
      }
    }
    
    this.subscribe()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    if (this.consumer) {
      this.consumer.disconnect()
      this.consumer = null
    }
    this.stopDurationTimer()
  }

  subscribe() {
    // Create or reuse consumer
    if (!this.consumer) {
      try {
        this.consumer = createConsumer()
        if (this.isDevelopment()) {
          console.log("✅ Created Action Cable consumer", this.consumer)
        }
      } catch (error) {
        console.error("❌ Failed to create Action Cable consumer:", error)
        return
      }
    }
    
    const subscriptionParams = { channel: "CrawlChannel", id: this.crawlIdValue }
    
    if (this.isDevelopment()) {
      console.log("Subscribing to CrawlChannel with params:", subscriptionParams)
    }
    
    try {
      this.subscription = this.consumer.subscriptions.create(
        subscriptionParams,
        {
          received: (data) => {
            if (this.isDevelopment()) {
              console.log("📨 Received crawl update:", data)
            }
            this.updateCrawl(data)
          },
          connected: () => {
            if (this.isDevelopment()) {
              console.log("✅ Connected to CrawlChannel for crawl", this.crawlIdValue)
            }
          },
          disconnected: () => {
            if (this.isDevelopment()) {
              console.log("❌ Disconnected from CrawlChannel")
            }
          },
          rejected: () => {
            console.error("❌ Rejected from CrawlChannel - check permissions")
          }
        }
      )
      if (this.isDevelopment()) {
        console.log("✅ Subscription created:", this.subscription)
      }
    } catch (error) {
      console.error("❌ Failed to create subscription:", error)
    }
  }

  updateCrawl(data) {
    if (this.isDevelopment()) {
      console.log("🔄 Updating crawl UI with data:", data)
    }
    
    // Update status
    if (data.status && this.hasStatusTarget) {
      this.statusTarget.textContent = this.capitalize(data.status)
      this.statusTarget.className = this.getStatusClass(data.status)
      
      // Stop duration timer if crawl is not running or paused
      if (data.status !== "running" && data.status !== "paused") {
        this.stopDurationTimer()
        this.lastStartedAt = null
      }
      
      if (this.isDevelopment()) {
        console.log("✅ Updated status to:", data.status)
      }
    }

    // Update pages crawled
    if (data.pages_crawled !== undefined && this.hasPagesCrawledTarget) {
      const oldValue = this.pagesCrawledTarget.textContent
      this.pagesCrawledTarget.textContent = data.pages_crawled
      if (this.isDevelopment()) {
        console.log(`✅ Updated pages_crawled from ${oldValue} to ${data.pages_crawled}`)
      }
    }

    // Update total pages
    if (data.total_pages !== undefined && this.hasTotalPagesTarget) {
      if (data.total_pages > 0) {
        this.totalPagesTarget.textContent = `/ ${data.total_pages}`
      } else {
        this.totalPagesTarget.textContent = ""
      }
    }

    // Update progress
    if (data.progress_percentage !== undefined) {
      if (this.hasProgressTarget) {
        this.progressTarget.textContent = `${data.progress_percentage}%`
      }
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${data.progress_percentage}%`
      }
    }

    // Update duration - show elapsed time for running/paused crawls, final duration for completed
    if (this.hasDurationTarget) {
      let duration = null
      if (data.status === "completed" && data.completed_at && data.started_at) {
        // Show final duration for completed crawls
        this.stopDurationTimer()
        duration = this.calculateDuration(data.started_at, data.completed_at)
      } else if ((data.status === "running" || data.status === "paused") && data.elapsed_duration !== undefined) {
        // Show elapsed time for running/paused crawls
        this.lastStartedAt = data.started_at
        duration = Math.round(data.elapsed_duration)
        this.startDurationTimer()
      } else if (data.started_at && (data.status === "running" || data.status === "paused")) {
        // Fallback: calculate from started_at if elapsed_duration not provided
        this.lastStartedAt = data.started_at
        duration = this.calculateElapsedDuration(data.started_at)
        this.startDurationTimer()
      }
      
      if (duration !== null) {
        this.durationTarget.textContent = `${duration}s`
      }
    }

    // Reload page when crawl completes to show full completed state
    // This ensures all server-rendered content (pages list, audit, etc.) is displayed
    if (data.status === "completed") {
      if (this.isDevelopment()) {
        console.log("Crawl completed, reloading page to show full completed state...")
      }
      window.location.reload()
      return
    }

    // Update stats if provided
    if (data.successful_pages !== undefined && this.hasSuccessfulPagesTarget) {
      this.successfulPagesTarget.textContent = data.successful_pages
    }
    if (data.error_pages !== undefined && this.hasErrorPagesTarget) {
      this.errorPagesTarget.textContent = data.error_pages
    }
    if (data.redirect_pages !== undefined && this.hasRedirectPagesTarget) {
      this.redirectPagesTarget.textContent = data.redirect_pages
    }

    // Update actions area (Stop/Resume/Cancel buttons) if provided
    if (data.actions_html) {
      const actionsElement = document.getElementById("crawl-actions")
      if (actionsElement) {
        actionsElement.innerHTML = data.actions_html
        if (this.isDevelopment()) {
          console.log("✅ Updated actions area with new buttons")
        }
      } else {
        if (this.isDevelopment()) {
          console.warn("⚠️ Actions element not found")
        }
      }
    }

    // Show progress bar if running
    if (data.status === "running" || data.status === "pending") {
      const statusCard = document.getElementById("crawl-status-card")
      if (statusCard) {
        let progressContainer = statusCard.querySelector(".mt-6")
        if (!progressContainer) {
          progressContainer = document.createElement("div")
          progressContainer.className = "mt-6"
          statusCard.appendChild(progressContainer)
        }
        let progressBar = progressContainer.querySelector("#crawl-progress-bar")
        if (!progressBar) {
          progressContainer.innerHTML = `
            <div class="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
              <div id="crawl-progress-bar" 
                   data-crawl-progress-target="progressBar"
                   class="h-full bg-indigo-600 transition-all duration-300" 
                   style="width: ${data.progress_percentage || 0}%"></div>
            </div>
          `
        } else if (data.progress_percentage !== undefined) {
          progressBar.style.width = `${data.progress_percentage}%`
        }
      }
    }
  }

  getStatusClass(status) {
    const baseClass = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset"
    switch (status) {
      case "completed":
        return `${baseClass} bg-green-500/10 text-green-400 ring-green-500/20`
      case "running":
        return `${baseClass} bg-blue-500/10 text-blue-400 ring-blue-500/20`
      case "paused":
        return `${baseClass} bg-yellow-500/10 text-yellow-400 ring-yellow-500/20`
      case "failed":
        return `${baseClass} bg-red-500/10 text-red-400 ring-red-500/20`
      case "cancelled":
        return `${baseClass} bg-orange-500/10 text-orange-400 ring-orange-500/20`
      default:
        return `${baseClass} bg-gray-500/10 text-gray-400 ring-gray-500/20`
    }
  }

  calculateDuration(startedAt, completedAt) {
    if (!startedAt || !completedAt) return null
    try {
      const start = new Date(startedAt)
      const end = new Date(completedAt)
      return Math.round((end - start) / 1000)
    } catch (e) {
      console.error("Error calculating duration:", e)
      return null
    }
  }

  calculateElapsedDuration(startedAt) {
    if (!startedAt) return null
    try {
      const start = new Date(startedAt)
      const now = new Date()
      return Math.round((now - start) / 1000)
    } catch (e) {
      console.error("Error calculating elapsed duration:", e)
      return null
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  startDurationTimer() {
    // Stop existing timer if any
    this.stopDurationTimer()
    
    // Update elapsed duration every second for running/paused crawls
    this.durationTimer = setInterval(() => {
      if (this.hasDurationTarget && this.lastStartedAt) {
        const duration = this.calculateElapsedDuration(this.lastStartedAt)
        if (duration !== null) {
          this.durationTarget.textContent = `${duration}s`
        }
      }
    }, 1000)
  }

  stopDurationTimer() {
    if (this.durationTimer) {
      clearInterval(this.durationTimer)
      this.durationTimer = null
    }
  }
}
